import React, {useCallback, useEffect, useState} from 'react';
import clsx from 'clsx';
import {FocusTrap} from '@mui/base';
import {
	Backdrop,
	Drawer as MuiDrawer,
	type DrawerProps as MuiDrawerProps,
	paperClasses,
} from '@mui/material';
import {duration} from '@mui/material/styles/createTransitions';
import {
	DragResize,
	DragResizeDirection,
	dragResizeHandleClasses,
} from '@netx/core-components/DragResize';
import {useDocumentKeyDown} from '@netx/core-components/hooks/useDocumentKeyDown';
import useWhenChangedEffect from '@netx/core-components/hooks/useWhenChangedEffect';
import type {IconButtonProps} from '@netx/core-components/IconButton';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {useConstCallback} from '../hooks';
import {DrawerAnchor, DrawerVariant} from './constants';
import {
	DRAWER_CLASS_NAME,
	drawerClasses,
	type DrawerClasses,
	getDrawerUtilityClass,
} from './drawerClasses';
import {DrawerToolbar, type DrawerToolbarProps} from './DrawerToolbar';

export type DrawerOwnerState = WithClasses<
	DrawerClasses,
	{
		open: boolean;
		anchor: DrawerAnchor;
		defaultSize?: number;
		resizing: boolean;
		temporary: boolean;
		mobile: boolean;
		mobileHack: boolean;
		hide?: boolean;
		disableHideContentsWhenClosed?: boolean;
	}
>;

const useUtilityClasses = (ownerState: DrawerOwnerState) => {
	const {classes, hide, mobile, mobileHack, open, resizing, temporary} =
		ownerState;
	const slots = {
		root: [
			'root',
			open && 'open',
			resizing && 'resizing',
			((mobile && !mobileHack) || temporary) && 'temporary',
			mobile && 'mobile',
			hide && 'hide',
		],
		content: ['content'],
		drawer: ['drawer', temporary && 'temporary'],
	};
	return composeClasses(slots, getDrawerUtilityClass, classes);
};

const DEFAULT_WIDTH = 265;

const DrawerRoot = styled(DragResize, {
	name: DRAWER_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => {
	const {
		hide,
		mobileHack,
		mobile,
		anchor,
		open,
		defaultSize,
		disableHideContentsWhenClosed,
	} = ownerState as DrawerOwnerState;
	return {
		display: 'flex',
		position: 'relative',
		whiteSpace: 'nowrap',
		pointerEvents: hide ? 'none !important' : undefined,
		[`&.${drawerClasses.open}`]: {
			width: defaultSize ?? DEFAULT_WIDTH,
		},
		[`&:not(.${drawerClasses.open})`]: {
			overflowX: 'hidden',
			width: hide ? `0px !important` : `61px !important`,
			cursor: 'pointer',
			...(disableHideContentsWhenClosed
				? {}
				: {
						[`& .${drawerClasses.content}`]: {
							display: 'none',
						},
					}),
		},
		[`&.${drawerClasses.temporary}`]: {
			[`&:not(.${drawerClasses.open})`]: {
				width: `0px !important`,
			},
			position: 'absolute',
			bottom: 0,
			top: 0,
			...(anchor === DrawerAnchor.RIGHT ? {right: 0} : {left: 0}),
		},
		...(mobileHack && mobile
			? {
					position: 'absolute',
					bottom: 0,
					top: 0,
					...(anchor === DrawerAnchor.RIGHT ? {right: 0} : {left: 0}),
				}
			: null),
		[`&:not(.${drawerClasses.temporary})`]: {
			[`&.${drawerClasses.mobile}`]: {
				position: 'fixed',
				minWidth: `calc(100% - 64px)`,
			},
			[`&:not(.${drawerClasses.mobile})`]: {
				position: 'absolute',
			},
			[`&:not(.${drawerClasses.open})`]: {
				width: `0px !important`,
			},
		},
		[`&:not(.${drawerClasses.temporary})`]: {
			minWidth: ownerState.hide ? `0px !important` : `61px !important`,
		},
	} as any;
});

const DrawerWrapper = styled(MuiDrawer)(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	minHeight: 0,
	// Undo some paper styles in the drawer
	[`& > .${paperClasses.root}`]: {
		flex: '1 1 auto',
		height: 'auto',
		minHeight: 0,
		overflow: 'visible',
		position: 'relative',
		width: '100%',
		zIndex: 'initial',
	},
}));

const DrawerContent = styled('div')(({theme}) => ({
	flex: '1 1 auto',
	display: 'flex',
	flexDirection: 'column',
	minHeight: 0,
	overflow: 'hidden',
	position: 'relative',
}));

type MuiDrawerMethods = Required<Pick<MuiDrawerProps, 'onClick' | 'onKeyDown'>>;
type IconButtonMethods = Required<Pick<IconButtonProps, 'onClick'>>;

export interface DrawerProps extends WithClasses<
	DrawerClasses,
	Omit<MuiDrawerProps, 'anchor' | 'classes' | 'variant'>
> {
	anchor?: DrawerAnchor;
	cancelAsClose?: boolean;
	disableHideContentsWhenClosed?: boolean;
	disableResize?: boolean;
	headerActions?: React.ReactNode;
	hide?: boolean;
	hideCollapseToggle?: boolean;
	hideDrawerToolbar?: boolean;
	keepMounted?: boolean;
	mobile?: boolean;
	mobileHack?: boolean;
	overflowParent: React.RefObject<HTMLElement>;
	secondaryTitle?: React.ReactNode;
	title?: string;
	width?: number;
	variant?: DrawerVariant;
	onCollapseToggle?: (collapsed: boolean) => void;
	onEntered?: () => void;
	onExited?: () => void;
	onResized?: (width: number) => void;
	DrawerToolbarProps?: Partial<DrawerToolbarProps>;
}

export const DrawerPrimitive = ({
	classes: _classes,
	children,
	className,

	anchor = DrawerAnchor.RIGHT,
	cancelAsClose,
	disableHideContentsWhenClosed,
	disableResize,
	headerActions,
	hide,
	hideCollapseToggle = false,
	hideDrawerToolbar = false,
	keepMounted = true,
	mobile = false,
	mobileHack = false,
	onCollapseToggle,
	onEntered,
	onExited,
	onResized,
	open = true,
	overflowParent,
	secondaryTitle,
	style,
	title = '',
	width = 0,
	variant = DrawerVariant.PERMANENT,
	DrawerToolbarProps,
	...props
}: DrawerProps) => {
	const [exited, setExited] = useState(!open);
	const [isOpen, setIsOpen] = useState(open);
	const [resizing, setResizing] = useState(false);

	const temporary = variant === DrawerVariant.TEMPORARY;

	useEffect(() => {
		setIsOpen(open);
	}, [open]);

	useWhenChangedEffect(isOpen, (isOpen) => {
		if (!isOpen) {
			setTimeout(() => {
				setExited(true);
				onExited?.();
			}, duration.leavingScreen);
		} else {
			setExited(false);
			setTimeout(() => {
				onEntered?.();
			}, duration.enteringScreen + 20);
		}

		onCollapseToggle?.(isOpen);
	});

	useDocumentKeyDown(
		(event) => {
			switch (event.key) {
				case 'Escape':
					event.preventDefault();
					event.stopPropagation();
					setIsOpen(false);
					return;
			}
		},
		{
			disabled: !isOpen || !cancelAsClose,
		},
	);

	const handleDrawerToggle = useConstCallback<IconButtonMethods['onClick']>(
		(event) => {
			event.preventDefault();
			event.stopPropagation();
			setIsOpen((isOpen) => !isOpen);
		},
	);

	const handleDrawerClick = useConstCallback<MuiDrawerMethods['onClick']>(
		(event) => {
			if (isOpen) return;
			event.preventDefault();
			event.stopPropagation();
			setIsOpen(true);
		},
	);

	const handleDrawerKeyDown = useConstCallback<MuiDrawerMethods['onKeyDown']>(
		(event) => {
			if (isOpen) return;
			const key = event.keyCode || event.which;
			// Only `enter` and `spacebar` keys
			if (![13, 32].includes(key)) return;
			event.preventDefault();
			event.stopPropagation();
			setIsOpen(true);
		},
	);

	const defaultSize = !disableResize ? width || DEFAULT_WIDTH : undefined;

	const ownerState = {
		classes: _classes,
		anchor,
		disableHideContentsWhenClosed,
		open: isOpen && !hide,
		resizing,
		temporary,
		mobile,
		mobileHack,
		hide,
		defaultSize,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<>
			{mobile ? (
				<Backdrop
					open={isOpen && !hide}
					onClick={handleDrawerToggle}
					style={{
						position: mobileHack ? 'absolute' : undefined,
					}}
				/>
			) : null}
			<DrawerRoot
				aria-hidden={hide ? 'true' : 'false'}
				className={clsx(classes.root, className)}
				ownerState={ownerState}
				disabled={!isOpen || mobile || disableResize}
				defaultSize={defaultSize}
				direction={
					anchor === DrawerAnchor.RIGHT
						? DragResizeDirection.LEFT
						: DragResizeDirection.RIGHT
				}
				minSize={temporary && !isOpen ? 0 : 265}
				overflowParentEl={overflowParent}
				size={temporary && !isOpen ? 0 : width}
				onResizeStarted={() => setResizing(true)}
				onResizeEnded={(size) => {
					setResizing(false);
					onResized?.(size);
				}}
			>
				<FocusTrap
					disableEnforceFocus={!cancelAsClose || !isOpen || hide}
					open={cancelAsClose === true && isOpen && !hide}
				>
					<DrawerWrapper
						className={classes.drawer}
						anchor={anchor}
						onClick={handleDrawerClick}
						onKeyDown={handleDrawerKeyDown}
						{...props}
						variant={DrawerVariant.PERMANENT}
						hideBackdrop={false}
					>
						{!hideDrawerToolbar && (
							<DrawerToolbar
								actions={headerActions}
								anchor={anchor}
								cancelAsClose={cancelAsClose}
								hidden={hide}
								hideCollapseToggle={hideCollapseToggle}
								open={disableHideContentsWhenClosed || (isOpen && !hide)}
								onCollapseToggle={handleDrawerToggle}
								title={title}
								{...DrawerToolbarProps}
							/>
						)}
						<DrawerContent className={classes.content}>
							{exited && !keepMounted ? null : children}
						</DrawerContent>
					</DrawerWrapper>
				</FocusTrap>
			</DrawerRoot>
		</>
	);
};

export const Drawer = React.memo(DrawerPrimitive);

export default Drawer;
